import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { MENU_ID_FILTERS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import type { Nav4DragAndDropMenuItemProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ActionButtons } from '../../common/ui/action-buttons/index.tsx';
import type { Nav4Filters$key } from './__generated__/Nav4Filters.graphql';
import { Nav4FiltersForLanding } from './for-landing/Nav4FiltersForLanding.tsx';
import { Nav4FiltersDefault } from './default/Nav4FiltersDefault.tsx';

export type Nav4FiltersProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
	queryRef: Nav4Filters$key;
};

export function Nav4Filters(props: Nav4FiltersProps): React.JSX.Element {
	const { isExpanded, queryRef, ...dndProps } = props;
	const data = useFragment<Nav4Filters$key>(
		graphql`
			fragment Nav4Filters on Query {
				...Nav4FiltersForLanding @include(if: $isFiltersExpanded)
				jira {
					hasIsFiltersExpanded: __typename @include(if: $isFiltersExpanded)
				}
			}
		`,
		queryRef,
	);
	const { isInitialSelectedPath } = useSidebarNav4();
	const actionButtons = useMemo(() => <ActionButtons />, []);
	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;

	if (!data.jira?.hasIsFiltersExpanded && isInitialSelectedPath(MENU_ID_FILTERS)) {
		// We need to make sure that we're only passing the conditional fragment when it is actually
		// available. Due to a lack of support the @alias Relay directive, we need to use another mechanism
		// and in this case we're using a hardcoded __typename to check if the fragment is available.
		// This is a workaround until we can use the @alias directive in Relay. If the hasIsProjectsExpanded
		// attribute is not available, then the fragment won't be either and we shouldn't pass it.
		return (
			<Nav4FiltersDefault
				actionsOnHover={actionButtons}
				isExpanded={isExpandedValue}
				{...dndProps}
			/>
		);
	}

	return isInitialSelectedPath(MENU_ID_FILTERS) ? (
		<Nav4FiltersForLanding
			actionsOnHover={actionButtons}
			isExpanded={isExpandedValue}
			queryRef={data}
			{...dndProps}
		/>
	) : (
		<Nav4FiltersDefault actionsOnHover={actionButtons} isExpanded={isExpandedValue} {...dndProps} />
	);
}
